/*
 * This file is part of eyeo's Web Extension Ad Blocking Toolkit (EWE),
 * Copyright (C) 2006-present eyeo GmbH
 *
 * EWE is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License version 3 as
 * published by the Free Software Foundation.
 *
 * EWE is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with EWE.  If not, see <http://www.gnu.org/licenses/>.
 */

import browser from "webextension-polyfill";
import {v4 as uuidv4} from "uuid";
import {MILLIS_IN_HOUR, MILLIS_IN_DAY} from "adblockpluscore/lib/time.js";
import {AbstractLogger, logLevelNumbersToNames, logLevelNamesToNumbers}
  from "../all/telemetry.js";

import {Prefs, init as initializePrefs} from "./prefs.js";
import * as info from "./info.js";
import {hasAcceptableAdsEnabled} from "./subscriptions.js";
import {EventDispatcher} from "./types.js";
import {Scheduler} from "../all/scheduler.js";
import {default as initializer} from "./initializer.js";
import {trace, error} from "./debugging.js";
import {default as experiments} from "./experiments.js";
import {premiumStatusCallback} from "./callbacks.js";
import {PersistentState} from "./persistence.js";

const TELEMETRY_STORAGE_KEY = "ewe:telemetry";

let scheduler = null;
let onError = new EventDispatcher(dispatch => {});

async function clearStorage() {
  await browser.storage.local.remove(TELEMETRY_STORAGE_KEY);
}

/**
 * @ignore
 * Starts the telemetry
 *
 * @param {Object} telemetry Telemetry configuration (URL, bearer)
 * @param {number} [interval]
 *        Ping interval in milliseconds (default: 12 hours)
 * @param {number} [errorRetryDelay]
 *        Error retry interval in milliseconds (default: 1 hour)
 */
export async function start(telemetry, interval = 12 * MILLIS_IN_HOUR,
                            errorRetryDelay = 1 * MILLIS_IN_HOUR) {
  if (!telemetry.url) {
    throw new Error("No telemetry `url` provided");
  }

  if (!telemetry.bearer) {
    throw new Error("No telemetry `bearer` provided");
  }

  if (scheduler) {
    return;
  }

  await initializePrefs();

  scheduler = new Scheduler({
    interval,
    errorRetryDelay,
    listener: () => ping(telemetry),
    async getNextTimestamp() {
      if (Prefs.telemetry_opt_out) {
        return Date.now() + MILLIS_IN_DAY;
      }

      let storageResult = await browser.storage.local.get(
        [TELEMETRY_STORAGE_KEY]
      );
      let storage = storageResult[TELEMETRY_STORAGE_KEY];
      let lastPing = storage && storage.lastPing;
      let lastError = storage && storage.lastError;

      if (lastError) {
        let nextRetryAfterError = new Date(lastError).getTime() +
            errorRetryDelay;
        let nowTimestamp = Date.now();
        let maxNextRetry = nowTimestamp + errorRetryDelay;
        if (nextRetryAfterError > maxNextRetry) {
          // This shouldn't happen if the clocks are working right. It implies
          // that the system clock has been set backwards since we stored that
          // timestamp. Let's correct the storage using our current clock
          // value. We shouldn't do this with lastPing because lastPing comes
          // from the server.
          nextRetryAfterError = maxNextRetry;
          await browser.storage.local.set({
            [TELEMETRY_STORAGE_KEY]: {
              ...storage,
              lastError: (new Date(nowTimestamp)).toISOString()
            }
          });
        }

        return nextRetryAfterError;
      }
      else if (lastPing) {
        return new Date(lastPing).getTime() + interval;
      }

      return null;
    }
  });
}

/**
 * @ignore
 */
export async function reset() {
  stop();
  await browser.storage.local.remove(TELEMETRY_STORAGE_KEY);
}

/**
 * @ignore
 */
export function stop() {
  if (!scheduler) {
    return;
  }

  scheduler.stop();
  scheduler = null;
}

function truncateTimes(timestamp) {
  if (!timestamp) {
    return timestamp;
  }

  return timestamp.split("T")[0] + "T00:00:00Z";
}

async function ping(telemetry) {
  trace({telemetry});
  await initializer.start();

  // This is a last line to ensure that we don't ever send a ping if someone has
  // opted out, even if the details of the scheduler change in the future. The
  // main opt out mechanism is telling the scheduler if it's time to ping or
  // not.
  if (Prefs.telemetry_opt_out) {
    return false;
  }

  const storageResult = await browser.storage.local.get(
    [TELEMETRY_STORAGE_KEY]
  );
  const storage = storageResult[TELEMETRY_STORAGE_KEY];

  let payload = await getExtensionMetadata();
  if (storage) {
    payload.first_ping = truncateTimes(storage.firstPing);
    payload.last_ping = truncateTimes(storage.lastPing);
    payload.previous_last_ping = truncateTimes(storage.previousLastPing);
    payload.last_ping_tag = storage.lastPingTag;
  }
  payload.ucid = (storage && storage.ucid) || uuidv4();

  let pingToken;
  try {
    let serverResponse = await postWithBearerToken(
      telemetry.url,
      telemetry.bearer,
      {payload}
    );
    pingToken = serverResponse.token;

    if (!pingToken) {
      throw new Error("Telemetry server response did not include a token.");
    }
  }
  catch (e) {
    // Not being sent to Sentry for now. If we do, we should find a way to
    // filter between unavoidable network errors and something actually being
    // wrong on the server.
    error(`Telemetry error: ${e.message}`);

    let lastError = (new Date()).toISOString();
    await browser.storage.local.set({
      [TELEMETRY_STORAGE_KEY]: {
        ...storage,
        lastError
      }
    });

    onError.emit({
      message: e.message,
      lastError
    });
    return false;
  }

  let newStorage = {
    firstPing: (storage && storage.firstPing) || pingToken,
    lastPing: pingToken,
    previousLastPing: storage && storage.lastPing,
    lastPingTag: uuidv4(),
    ucid: payload.ucid
  };

  await browser.storage.local.set({
    [TELEMETRY_STORAGE_KEY]: newStorage
  });
  return true;
}

/**
 * Uses the fetch API to make a POST request to the specified endpoint.
 *
 * @param {string} url URL for the request.
 * @param {string} bearer Bearer token for use in Authentication header.
 * @param {Object} body Request body, will be serialized into JSON to send.
 * @return {Promise<Object>} Promise resolves with the object returned from the
 *   server. Rejects if the request fails, or the response has an error status.
 * @ignore
 */
export async function postWithBearerToken(url, bearer, body) {
  trace({url, bearer, body});
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${bearer}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    throw new Error(
      `Telemetry server responded with error status ${response.status}.`
    );
  }

  return await response.json();
}

async function getExperimentsAssignments() {
  const assignments = await experiments.getAssignments();
  return Object.entries(assignments).map(assignment => {
    return {
      experiment_id: assignment[0],
      variant_id: assignment[1]
    };
  });
}

async function getExtensionMetadata() {
  let platform;
  let platformVersion;

  if (navigator.userAgentData) {
    const platformInfo = await navigator.userAgentData.getHighEntropyValues([
      "platform",
      "platformVersion"
    ]);

    platform = platformInfo.platform;
    platformVersion = platformInfo.platformVersion;
  }
  else {
    const platformInfo = await browser.runtime.getPlatformInfo();
    platform = platformInfo.os;
  }

  const payload = {
    // The OS. Windows, Linux, etc.
    platform,

    // The version of the system OS.
    platform_version: platformVersion,

    // The browser. Chrome, Firefox, etc.
    application: info.application,

    // The version of the browser.
    application_version: info.applicationVersion,

    // Used to identify this specific project.
    addon_name: info.sdkName,

    // The Engine's current version. See the Dotenv plugin in webpack.config.js
    addon_version: info.sdkVersion,

    // The name of the extension using the Engine.
    extension_name: info.addonName,

    // The extension's version.
    extension_version: info.addonVersion,

    // Depends on having the acceptable ads subscription enabled.
    aa_active: await hasAcceptableAdsEnabled(),

    // The currently assigned split experiments
    experiments: await getExperimentsAssignments()
  };

  if (premiumStatusCallback) {
    const premiumStatus = await premiumStatusCallback();
    payload.premium_status = premiumStatus ? "premium" : "free";
  }

  return payload;
}

// Console logging is primarily for devs who are currently adding logging to
// be able to see the logs, or for devs trying to reproduce something that
// they saw in the server logs.
let consoleLogLevel = new PersistentState(
  "ewe:telemetry-logging-console-level",
  browser.storage.local
);

function loggingEnabled(module, level, loggingConfig) {
  let possibleLevels = [
    loggingConfig.globalLevel,
    loggingConfig.byModule ? loggingConfig.byModule[module] : null
  ];
  return possibleLevels.some(
    configLevel => configLevel && level >= configLevel
  );
}

async function consoleLog(logTime, module, level, event, data) {
  if (loggingEnabled(module, level, await consoleLogLevel.getStateLoaded())) {
    const logParams = [
      logTime,
      module,
      logLevelNumbersToNames[level],
      event];

    if (typeof data != "undefined") {
      logParams.push(data);
    }

    // eslint-disable-next-line no-console
    console.log(...logParams);
  }
}

/**
 * @ignore
 * Records an event.
 *
 * Depending on the current logging config, this may send the log to a remote
 * server, or print the log on the console.
 *
 * There may be internal asynchronous processes before the log is actually sent
 * anywhere or displayed on the console, so don't expect logs to have happened
 * already when this function returns.
 *
 * @param {string} logTime When this log occurred.
 * @param {string} module The module in which the event occurred.
 * @param {number} level The logging level, from 1=debug to 4=warn.
 * @param {string} event The event being logged. This should be unique.
 * @param {string|number|Object|Array|boolean} [data]
 */
export function serverLogValidatedEvent(logTime, module, level, event, data) {
  // This is currently only console logging, but the intention is for the server
  // logging to be added here soon.
  void consoleLog(logTime, module, level, event, data);
}

/**
 * Represents a server logger. This is used to send data to a our
 * telemetry server. The exact URL will be configured in this file's
 * start function. Console logging is also provided for convenience.
 *
 * @param {string} module The module that this logger is for.
 */
export class ServerLogger extends AbstractLogger {
  constructor(module) {
    super(module);
  }

  _validatedLog(logTime, level, event, data) {
    serverLogValidatedEvent(logTime, this._module, level, event, data);
  }
}

export default {
  /**
  * @typedef {Object} TelemetryPingError
  * @property {string} message The error message, describing the failure.
  * @property {string} lastError The timestamp of the error.
  */

  /**
   * Emitted when sending a telemetry ping fails.
   *
   * @event
   * @type {EventDispatcher<TelemetryPingError>}
   */
  onError,

  /**
   * Opt-out from telemetry. Default is `true` (used opted out)
   * @param {boolean} value Pass `true` to opt-out, pass `false` to opt in.
   * @returns {Promise} Promise that resolves when the setting is set.
   */
  async setOptOut(value) {
    Prefs.telemetry_opt_out = value;

    if (scheduler && !value) {
      scheduler.checkNow();
    }

    if (value) {
      await clearStorage();
    }
  },

  /**
    * @class {ServerLogger}
    */
  ServerLogger,

  /**
   * Resets the console logging level to "off".
   * @returns {Promise} Promise that resolves when the setting is set.
   */
  async resetConsoleLogLevel() {
    consoleLogLevel.clearState();
    await consoleLogLevel.save();
  },

  /**
   * Sets the intended console logging level.
   *
   * If both the global log level and a module-specific log level are set, the
   * most verbose one takes priority.
   *
   * Setting the log level implies that all less verbose levels are also
   * enabled. For example, enabling "info" logging will also enable "behavior"
   * and "warn" logging.
   *
   * @param {string} level The level you want to log at. Can be one of
   *                       "debug", "info", "behavior", "warn", or "off".
   * @param {string?} module The specific module you want to set the log level
   *                         for. Not passing this sets the global log level.
   * @returns {Promise} Promise that resolves when the setting is set.
   */
  async setConsoleLogLevel(level, module) {
    let newLevel = logLevelNamesToNumbers[level];
    if (!newLevel) {
      throw new Error(`Unknown log level: ${level}`);
    }

    let config = await consoleLogLevel.getStateLoaded();

    if (module) {
      if (newLevel == logLevelNamesToNumbers.off) {
        if (config.byModule) {
          delete config.byModule[module];
        }
      }
      else {
        if (!config.byModule) {
          config.byModule = {};
        }

        config.byModule[module] = newLevel;
      }
    }
    else if (newLevel == logLevelNamesToNumbers.off) {
      delete config.globalLevel;
    }
    else {
      config.globalLevel = newLevel;
    }

    await consoleLogLevel.save();
  }
};
